import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useDebounce from '../../hooks/useDebounce';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useAlert } from '../../context/alert';
import { useSubcategoriesInfiniteQuery } from '../../hooks/useSubcategoriesQuery';
import { CategoryResponse } from '../../types/api/categories';
import { SubcategoryResponse } from '../../types/api/catalogs';
import { SubCategoryView } from './components/SubCategoryView';
import { pageSize } from '../../services/settings';
import { Placeholder, createErrorPlaceholder } from '../basic';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import TableActionsBanner from '../common/TableActionsBanner';
import { SubcategoryModal } from './components/SubcategoryModal';
import { TransferAdsModal } from './components/TransferAdsModal';
import { renderTruncatedText } from '../../helpers/strings';
import { Stack, TextInput, Button, SkeletonBox, Breadcrumbs } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon, PlusIcon } from '@primer/octicons-react'
import { fetchCategory } from '../../services/categories';
import { deleteSubcategory } from '../../services/catalogs';

export default function SubCategoriesList() {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const listRef = useRef<HTMLDivElement | null>(null);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryResponse | undefined>(undefined);
    const [openTransferModal, setOpenTransferModal] = React.useState(false)
    const [parentCategory, setParentCategory] = useState<CategoryResponse | undefined>(undefined);
    const [openSubcategoryModal, setOpenSubcategoryModal] = React.useState(false)

    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();
    const fetchedIdRef = useRef<string | null>(null);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const { mutate: fetchCategoryById, isPending: isLoadingProfile } = useMutation({
        mutationFn: fetchCategory,
        onSuccess: (data) => {
            setParentCategory(data)
        },
        onError: (error: any) => {

        }
    });

    useEffect(() => {
        const id = searchParams.get('id');
        if (id && fetchedIdRef.current !== id && !isLoadingProfile) {
            fetchedIdRef.current = id;
            fetchCatalogData();
            refetch()
        }
    }, [searchParams, fetchCategoryById, isLoadingProfile]);

    const fetchCatalogData = () => {
        const id = searchParams.get('id');
        if (id) {
            fetchCategoryById(id);
        }
    }

    const {
        isLoading: isLoadingAds,
        data: sectionsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = useSubcategoriesInfiniteQuery(
        fetchedIdRef.current ?? "",
        debouncedSearchQuery, // search
        pageSize, //limit
        {
            enabled: fetchedIdRef.current !== undefined
        }
    );

    const { mutate: deleteSubcategoryRequest } = useMutation({
        mutationFn: ({ id }: {
            id: string,
        }) => deleteSubcategory({ ids: [id], replaceBy: undefined }),
        onSuccess: (data) => {
            subcategoryDidDeleted(false)
        }
    });

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const handleScroll = useInfiniteScroll(
        listRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        sectionsData
    );


    function createConfirmDeleteAlertProps(subcategory: SubcategoryResponse) {
        return {
            title: 'Точно хотите удалить подраздел?',
            count: undefined,
            buttons: [
                <Button key="no-button" variant="default" onClick={dismissConfirmBlockAlert}>
                    Нет
                </Button>,
                <Button key="yes-button" variant="danger" onClick={() => deleteOrShowTransfer(subcategory)}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
            }
        }
    }

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const dismissConfirmBlockAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
    }, [actionsAlertId]);

    const openSubcategoryPage = (subcategory: SubcategoryResponse) => {
        navigate(`/catalogs/ads?catalogId=${subcategory.id}&categoryId=${subcategory.categoryId}`);
    }
    const createNewSubcategory = () => {
        setSelectedSubcategory(undefined)
        setOpenSubcategoryModal(true)
    }
    const editSubcategory = (subcategory: SubcategoryResponse) => {
        setSelectedSubcategory(subcategory)
        setOpenSubcategoryModal(true)
    }
    const deleteSubcategoryAction = (subcategory: SubcategoryResponse) => {
        showDeleteSubcategoryAlert(subcategory)
    }

    const subcategoryDismissed = () => {
        setSelectedSubcategory(undefined)
        setOpenSubcategoryModal(false)
    }

    const subcategorySaved = () => {
        setSelectedSubcategory(undefined)
        setOpenSubcategoryModal(false)
        refetch()
    }

    const deleteOrShowTransfer = (subcategory: SubcategoryResponse) => {
        if (subcategory.adsCount === 0) {
            deleteSubcategoryRequest({ id: subcategory.id })
        } else {
            openTransferAdsModal(subcategory)
        }
    }
    const openTransferAdsModal = (subcategory: SubcategoryResponse) => {
        dismissConfirmBlockAlert()
        setSelectedSubcategory(subcategory)
        setOpenTransferModal(true)
    }

    const showDeleteSubcategoryAlert = useCallback((category: SubcategoryResponse) => {
        dismissConfirmBlockAlert()

        let props = createConfirmDeleteAlertProps(category)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    const transferModalDidClosed = () => {
        setOpenTransferModal(false)
        setSelectedSubcategory(undefined)
    }

    const subcategoryDidDeleted = (withTransfer: boolean) => {
        transferModalDidClosed()
        refetch()
        dismissConfirmBlockAlert()
        setInfo(withTransfer ? 'Подраздел удален и объявления перенесены' : 'Подраздел удален')
    }

    const emptyListDescription = `В разделе «${parentCategory?.title}» не создано
    ни одного подраздела`;
    const emptySearchListDescription = `По вашему запросу не найдено
    подходящих подразделов`;

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <Breadcrumbs>
                        <Breadcrumbs.Item href="/sections/">Все разделы</Breadcrumbs.Item>
                        <Breadcrumbs.Item href="#" selected>
                            {renderTruncatedText(parentCategory?.title, 32)}
                        </Breadcrumbs.Item>
                    </Breadcrumbs>
                    <Stack direction="horizontal">
                        <TextInput
                            leadingVisual={SearchIcon}
                            trailingAction={
                                searchQuery.length > 0 ? (
                                    <TextInput.Action
                                        onClick={() => {
                                            setSearchQuery('');
                                        }}
                                        icon={XCircleFillIcon}
                                        aria-label="Очистить"
                                    />
                                ) : undefined
                            }
                            value={searchQuery}
                            placeholder='Поиск'
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                            }}
                        />
                        <Button
                            variant="default"
                            size="small"
                            onClick={createNewSubcategory}
                            leadingVisual={PlusIcon}
                        >
                            Создать подраздел
                        </Button>
                    </Stack>
                </Stack>
            }
            content={
                <Stack>

                    {selectedSubcategory && parentCategory && openTransferModal && (<TransferAdsModal
                        isOpen={openTransferModal}
                        category={parentCategory}
                        subcategory={selectedSubcategory}
                        onDidDeleted={() => subcategoryDidDeleted(true)}
                        onClose={transferModalDidClosed}
                    />)}
                    {openSubcategoryModal && parentCategory && (<SubcategoryModal
                        isOpen={openSubcategoryModal}
                        parentCategory={parentCategory}
                        subcategory={selectedSubcategory}
                        onDidSaved={subcategoryDismissed}
                        onClose={subcategorySaved}
                    />)}
                    {!isLoadingAds && !isFetchingNextPage && (sectionsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Подразделы не найдены'
                            description={searchQuery.length > 0 ? emptySearchListDescription : emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    <div
                        ref={listRef}
                        onScroll={handleScroll}
                        className="mx-auto max-w-screen-xl w-full overflow-y-auto"
                        style={{
                            height: 'calc(100vh - 120px)'
                        }}
                    >
                        {sectionsData?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: SubcategoryResponse, index) => (
                                    <SubCategoryView
                                        key={e.id}
                                        subcategory={e}
                                        onSubCategoryClick={() => openSubcategoryPage(e)}
                                        onEditClick={() => editSubcategory(e)}
                                        onDeleteClick={() => deleteSubcategoryAction(e)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoadingAds) &&
                        <div className="max-h-screen overflow-y-auto">
                            <SkeletonBox height="4rem" />
                        </div>
                    }
                </Stack>
            }
        ></RegularPageLayout>

    )
}